import CommonSider from '@/components/Sider/Sider';
import { MenuProps, Modal } from 'antd';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import LocalStorageUtility from '@/utils/localStorage';
import { useMobileDeviceCheck } from 'src/common/hooks/useMobile';
import { useEffect } from 'react';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';
import {
	GlobalOutlined,
	ApiOutlined,
	MessageOutlined,
	MailOutlined,
	PhoneOutlined,
	TrademarkCircleOutlined,
	TrademarkOutlined,
	CrownOutlined,
	WalletOutlined,
	PicLeftOutlined,
	YoutubeOutlined,
	ImportOutlined,
	HarmonyOSOutlined,
	LogoutOutlined,
	RocketOutlined,
	MoneyCollectOutlined,
	DollarOutlined,
	BankOutlined,
	PicCenterOutlined,
	DeploymentUnitOutlined,
	LockOutlined,
} from '@ant-design/icons';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import { FeatureFlag } from 'src/enums/EFeatureFlags';
import { resetTranslationCookies } from '@/utils/translation';

import './ResellerSider.scss';
import { DESKTOP_LOGO_S3_URL, MOBILE_LOGO_S3_URL } from 'src/constants';

const ResellerSider: React.FC<{
	onMenuSelect: (key: ResellerSiderItem) => void;
	activeMenuItem: ResellerSiderItem;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	hideOutbound: boolean;
	collapsed?: boolean;
}> = ({ onMenuSelect, activeMenuItem, setLoading, hideOutbound = false, collapsed = false }) => {
	const { isMobile } = useMobileDeviceCheck();
	const currentReseller = useAppSelector(getResellerData);

	const hasCompletedOnboarding =
		'hasCompletedOnboarding' in currentReseller ? currentReseller?.hasCompletedOnboarding : true;

	useEffect(() => {
		setLoading(false);
	}, []);

	// Check if the feature flag is enabled
	const isCustomDomainEnabled = useFeatureFlag(FeatureFlag.CUSTOM_DOMAIN_FLAG);

	const items: MenuProps['items'] = [
		{
			key: ResellerSiderItem.ONBOARDING,
			icon: <RocketOutlined />,
			label: (
				<div
					className={`font-medium ${activeMenuItem === ResellerSiderItem.ONBOARDING ? 'text-white' : 'text-blue-600'}`}
				>
					{ResellerSiderItem.ONBOARDING}
				</div>
			),
			className: `bg-[#F1F8FF] hover:bg-[#F1F8FF] ${
				activeMenuItem !== ResellerSiderItem.ONBOARDING ? 'has-ping-animation' : ''
			} ${activeMenuItem === ResellerSiderItem.ONBOARDING ? '!bg-blue-600' : ''}`,
		},
		{
			key: ResellerSiderItem.MY_RECEPTIONISTS,
			icon: <PicLeftOutlined />,
			label: ResellerSiderItem.MY_RECEPTIONISTS,
		},
		{
			key: ResellerSiderItem.PLANS,
			icon: <CrownOutlined />,
			label: ResellerSiderItem.PLANS,
			children: [
				{
					key: ResellerSiderItem.PLAN_CREATION,
					icon: <DollarOutlined />,
					label: ResellerSiderItem.PLAN_CREATION,
				},
				{
					key: ResellerSiderItem.PLAN_VIEW,
					icon: <MoneyCollectOutlined />,
					label: ResellerSiderItem.PLAN_VIEW,
				},
			],
		},
		{
			key: ResellerSiderItem.BRANDING_CONFIG,
			icon: <TrademarkOutlined />,
			label: ResellerSiderItem.BRANDING_CONFIG,
			children: [
				{
					key: ResellerSiderItem.BRANDING,
					icon: <TrademarkCircleOutlined />,
					label: ResellerSiderItem.BRANDING,
				},
				{
					key: ResellerSiderItem.PHONE_NUMBERS,
					icon: <PhoneOutlined />,
					label: ResellerSiderItem.PHONE_NUMBERS,
				},
				{
					key: ResellerSiderItem.EMAIL_CONFIG,
					icon: <MailOutlined />,
					label: ResellerSiderItem.EMAIL_CONFIG,
				},
				{
					key: ResellerSiderItem.INTERCOM_SUPPORT,
					icon: <MessageOutlined />,
					label: ResellerSiderItem.INTERCOM_SUPPORT,
				},
			],
		},
		...(isCustomDomainEnabled
			? [
					{
						key: ResellerSiderItem.CUSTOM_DOMAIN,
						icon: <GlobalOutlined />,
						label: ResellerSiderItem.CUSTOM_DOMAIN,
					},
				]
			: []),
		...(useFeatureFlag('ResellerWebhook')
			? [
					{
						key: ResellerSiderItem.WEBHOOKS,
						icon: <ApiOutlined />,
						label: ResellerSiderItem.WEBHOOKS,
					},
				]
			: []),
		...(currentReseller?.permanently_unbranded || currentReseller?.branded
			? []
			: [
					{
						key: ResellerSiderItem.SUBSCRIPTION,
						icon: <BankOutlined />,
						label: ResellerSiderItem.SUBSCRIPTION,
					},
				]),
		{
			key: ResellerSiderItem.ACADEMY,
			icon: <YoutubeOutlined />,
			label: ResellerSiderItem.ACADEMY,
		},

		{
			key: ResellerSiderItem.IMPORT_NUMBER,
			icon: <ImportOutlined />,
			label: ResellerSiderItem.IMPORT_NUMBER,
		},
		{
			key: ResellerSiderItem.CONTACT_SUPPORT,
			icon: <PhoneOutlined />,
			label: ResellerSiderItem.CONTACT_SUPPORT,
		},
	];

	if (
		(currentReseller?.inboundSubscribedProductId &&
			currentReseller?.inboundSubscriptionStatus !== 'canceled') ||
		(currentReseller?.outboundSubscribedProductId &&
			currentReseller?.outboundSubscriptionStatus !== 'canceled')
	) {
		items.push({
			key: ResellerSiderItem.MANAGE_CREDITS,
			icon: <WalletOutlined />,
			label: ResellerSiderItem.MANAGE_CREDITS,
		});
	}

	if (!hideOutbound) {
		const outboundItems = {
			key: ResellerSiderItem.OUTBOUND_ROOT,
			icon: <HarmonyOSOutlined />,
			label: ResellerSiderItem.OUTBOUND_ROOT,
			children: [
				{
					key: ResellerSiderItem.OUTBOUND_MY_CAMPAIGNS,
					icon: <PicCenterOutlined />,
					label: ResellerSiderItem.OUTBOUND_MY_CAMPAIGNS,
				},
				{
					key: ResellerSiderItem.OUTBOUND_BYOT,
					icon: <DeploymentUnitOutlined />,
					label: ResellerSiderItem.OUTBOUND_BYOT,
				},
			],
		};

		items.push(outboundItems);
	}

	const handleLogout = () => {
		Modal.confirm({
			title: 'Are you sure you want to logout?',
			content: 'You will be redirected to the login page.',
			okText: 'Yes, logout',
			cancelText: 'Cancel',
			onOk: () => {
				LocalStorageUtility.clearLocalData();
				resetTranslationCookies();
			},
		});
	};

	const handleClick: MenuProps['onClick'] = (e) => {
		if (e.key === 'Logout') {
			handleLogout();
		} else if (!hasCompletedOnboarding && e.key !== ResellerSiderItem.ONBOARDING) {
			Modal.confirm({
				title: 'Complete Onboarding First',
				content:
					'You need to complete the onboarding process and then you will be able to access this feature.',
				okText: activeMenuItem === ResellerSiderItem.ONBOARDING ? 'Ok' : 'Go to Onboarding',
				cancelText: 'Cancel',
				onOk: () => {
					if (activeMenuItem !== ResellerSiderItem.ONBOARDING) {
						onMenuSelect(ResellerSiderItem.ONBOARDING);
					}
				},
			});
			return;
		} else {
			onMenuSelect(e.key as ResellerSiderItem);
		}
	};

	const AppLogo = (
		<div className="px-4 py-2">
			{isMobile ? (
				<img src={MOBILE_LOGO_S3_URL} alt="ML RSS" />
			) : (
				<img src={DESKTOP_LOGO_S3_URL} alt="DL RSS" />
			)}
		</div>
	);

	let refinedItems = currentReseller?.isOnboardingComplete
		? [
				...items.slice(1),
				{
					key: ResellerSiderItem.ONBOARDING,
					icon: <RocketOutlined />,
					label: ResellerSiderItem.ONBOARDING,
				},
			]
		: items;

	const logoutItem = {
		key: 'Logout',
		icon: <LogoutOutlined />,
		label: 'Logout',
		className: '!text-red-500 hover:!text-red-600',
		danger: true,
	};

	refinedItems = [
		...refinedItems.map((item: any) => {
			if (!hasCompletedOnboarding && ![ResellerSiderItem.ONBOARDING, 'Logout'].includes(item.key)) {
				return {
					...item,
					disable: !hasCompletedOnboarding,
					className: item.className + ' opacity-50 !cursor-not-allowed',
					icon: !hasCompletedOnboarding ? (
						<LockOutlined
							className={item.key === ResellerSiderItem.BRANDING_CONFIG ? 'ml-2' : ''}
						/>
					) : (
						item.icon
					),
					children: item.children?.map((child: any) => {
						return {
							...child,
							disable: !hasCompletedOnboarding,
							className: child.className + ' opacity-50 !cursor-not-allowed',
							icon: !hasCompletedOnboarding ? <LockOutlined /> : child.icon,
						};
					}),
				};
			}
			return item;
		}),
		logoutItem,
	];

	return (
		<>
			<div className={`${collapsed ? 'sider-collapsed' : ''} relative min-h-screen`}>
				<CommonSider
					items={[...refinedItems]}
					logo={AppLogo}
					hideLogo={
						!currentReseller?.emailAddress ||
						currentReseller?.permanently_unbranded ||
						currentReseller?.branded
					}
					width={isMobile ? 200 : 280}
					activeMenuItem={activeMenuItem}
					handleClick={handleClick}
					openedOnboarding={true}
					tourOpen={true}
					setOpenedOnboarding={() => {}}
					resellerSider
					className={`reseller-sider ${collapsed ? 'sider-collapsed' : ''}`}
				/>
			</div>
		</>
	);
};

export default ResellerSider;
